import useUserStore from '@/user/userStore'
import Intercom, { show, hide, onHide, shutdown } from '@intercom/messenger-js-sdk'
import { useEffect, useRef } from 'react'
import { setIntercomBaselineHidden } from '@/common/utils/intercom'
import { useIsSmallScreen } from '@/common/ui/useIsSmallScreen'

// `onHide` returns an unsubscribe function; type it explicitly for safety.
type Unsubscribe = () => void

type Props = {
    showWidget?: boolean
    onHideWidget?: () => void
}

export function IntercomWidget({ showWidget = false, onHideWidget }: Props) {
    const { user } = useUserStore()
    // Desktop keeps Intercom's native launcher; mobile replaces it with a top-menu
    // button (MobileIntercomButton), so the default launcher/notifications must
    // only be suppressed on small screens.
    const isSmallScreen = useIsSmallScreen(768)

    // Keep a ref to the latest callback so the single registered handler
    // can call the most recent `onHideWidget` without re-registering.
    const onHideRef = useRef(onHideWidget)
    useEffect(() => {
        onHideRef.current = onHideWidget
    }, [onHideWidget])

    // Initialize Intercom when user changes; teardown on unmount or when user becomes null
    useEffect(() => {
        Intercom({
            app_id: 'o0uvgmtg',
            user_id: user?.userId,
            name: user?.firstName,
            email: user?.emailId,
            hide_default_launcher: isSmallScreen,
            hide_notifications: isSmallScreen,
        })

        return () => {
            try {
                shutdown()
            } catch {
                // ignore
            }
        }
        // Seed the initial paint only; ongoing visibility is handled by
        // setIntercomBaselineHidden below without rebooting the Intercom session.
        // depend on identifying user fields only
    }, [user?.userId, user?.firstName, user?.emailId, isSmallScreen])

    // Register onHide only once and use the ref to call the latest callback.
    useEffect(() => {
        const off = onHide(() => {
            try {
                if (onHideRef.current) {
                    onHideRef.current()
                }
            } catch {
                // ignore
            }
        }) as Unsubscribe | void

        return () => {
            try {
                if (typeof off === 'function') {
                    off()
                }
            } catch {
                // ignore
            }
        }
    }, [])

    useEffect(() => {
        if (showWidget) {
            try {
                show()
                setIntercomBaselineHidden(false)
            } catch {
                // ignore
            }
        } else {
            try {
                hide()
                setIntercomBaselineHidden(isSmallScreen)
            } catch {
                // ignore
            }
        }
    }, [showWidget, isSmallScreen])

    return null
}
